import { Grid2 as Grid, FormControlLabel, Switch, MenuItem } from "@mui/material"
import Button from "@mui/material/Button"
import { Field, Formik } from "formik"
import { useMemo } from "react"
import * as yup from "yup"
import { Rimediazione } from "../../types"
import { transformErrorForForm } from "../../utils/utils"
import { FormInDialogProps } from "../DialogForm"
import { FormikDatePicker, FormikSelect, FormikTextField } from "./formsHelpers"
import { useTranslation } from "react-i18next"
import { ACLuogo } from "./autocompletes"
import { FormikSelectVocabolario } from "./formsHelpers"

export default function RimediazioneForm({ item, save, ref }: FormInDialogProps<Rimediazione>) {
  const { t } = useTranslation()
  const initialValues = useMemo(() => {
    return (
      item ?? {
        data: undefined,
        uscita: undefined,
        rientro: undefined,
        lab: "",
        esterna: false,
        sonoro: false,
        decad_col: false,
        scan_prewet: false,
        note_decad: "",
        note: "",
        luogo: undefined,
        strumento_scansione: null,
        formato_img: null,
        framerate: null,
        risoluzione: "",
        codec: null,
        tipo_suono: null,
        tecnica_suono: null,
      }
    )
  }, [item])

  const schema = useMemo(() => yup.object().shape({}), [t])

  return (
    <Formik
      validationSchema={schema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, formikBag) => {
        return save(values as Rimediazione).catch((e) => {
          const localError = transformErrorForForm(e)
          formikBag.setErrors(localError)
        })
      }}
    >
      {({ handleSubmit, values, setFieldValue }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={1} sx={{ py: 2 }}>
              <Grid size={6}>
                <Field
                  component={FormikDatePicker}
                  name="data"
                  label={t("general.data")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="tipo"
                  label={t("rimediazione.tipo")}
                  component={FormikSelectVocabolario}
                  vocabolario="tipo_rimediazione"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="velocita_rimediazione"
                  label={t("rimediazione.velocita_rimediazione")}
                  component={FormikSelectVocabolario}
                  vocabolario="frequenza_rimediazione"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="esposizione"
                  label={t("rimediazione.esposizione")}
                  component={FormikSelectVocabolario}
                  vocabolario="esposizione"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="flusso"
                  label={t("rimediazione.flusso")}
                  component={FormikSelect}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                  <MenuItem value={"3"}>3</MenuItem>
                  <MenuItem value={"4"}>4</MenuItem>
                  <MenuItem value={"5"}>5</MenuItem>
                </Field>
              </Grid>

              <Grid size={6}>
                <Field
                  name="formato_img"
                  label={t("rimediazione.formato_img")}
                  component={FormikSelectVocabolario}
                  vocabolario="formato_immagine"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="framerate"
                  label={t("rimediazione.framerate")}
                  component={FormikSelectVocabolario}
                  vocabolario="framerate"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="risoluzione"
                  label={t("rimediazione.risoluzione")}
                  component={FormikTextField}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="codec"
                  label={t("rimediazione.codec")}
                  component={FormikSelectVocabolario}
                  vocabolario="codec"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="tipo_suono"
                  label={t("rimediazione.tipo_suono")}
                  component={FormikSelectVocabolario}
                  vocabolario="presenza_audio"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <FormControlLabel
                  control={
                    <Switch checked={values.sonoro} onChange={(e) => setFieldValue("sonoro", e.target.checked)} />
                  }
                  label={t("rimediazione.sonoro")}
                />
              </Grid>
              <Grid size={6}>
                <FormControlLabel
                  control={
                    <Switch checked={values.decad_col} onChange={(e) => setFieldValue("decad_col", e.target.checked)} />
                  }
                  label={t("rimediazione.decad_col")}
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="graffi"
                  label={t("rimediazione.graffi")}
                  component={FormikSelectVocabolario}
                  vocabolario="graffi_rilevati"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.scan_prewet}
                      onChange={(e) => setFieldValue("scan_prewet", e.target.checked)}
                    />
                  }
                  label={t("rimediazione.scan_prewet")}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  name="note_decad"
                  label={t("rimediazione.note_decad")}
                  component={FormikTextField}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={6}
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="strumento_scansione"
                  label={t("rimediazione.strumento_scansione")}
                  component={FormikSelectVocabolario}
                  vocabolario="strumenti_scansione"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <Field
                  name="note"
                  label={t("general.note")}
                  component={FormikTextField}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={6}
                />
              </Grid>

              <Grid size={6}>
                <FormControlLabel
                  control={
                    <Switch checked={values.esterna} onChange={(e) => setFieldValue("esterna", e.target.checked)} />
                  }
                  label={t("rimediazione.esterna")}
                />
              </Grid>

              <Grid size={8}>
                <Field
                  name="lab"
                  label={t("copia.lab")}
                  component={FormikTextField}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={4}>
                <ACLuogo
                  margin="dense"
                  variant="outlined"
                  label={t("general.luogo")}
                  value={values.luogo}
                  onChange={(value) => {
                    setFieldValue("luogo", value)
                  }}
                />
              </Grid>

              <Grid size={6}>
                <Field
                  component={FormikDatePicker}
                  name="uscita"
                  label={t("copia.uscita")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                />
              </Grid>

              <Grid size={6}>
                <Field
                  component={FormikDatePicker}
                  name="rientro"
                  label={t("copia.rientro")}
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Button ref={ref} type="submit" color="primary" sx={{ display: "none" }}></Button>
          </form>
        )
      }}
    </Formik>
  )
}
